/* ==========================================================================
   AURA — vanilla replacements for the original React/framer-motion behaviour
   - scroll reveal (AnimatedSection)
   - sticky header state
   - mobile drawer
   - FAQ accordion
   - offset anchor scrolling
   ========================================================================== */

(function () {
  'use strict';

  var HEADER_OFFSET = 110;
  var DRAWER_OFFSET = 20;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal */

  function revealNow(el) {
    el.classList.add('is-visible');
  }

  function initReveal(root) {
    var targets = root.querySelectorAll('.aura-reveal:not([data-aura-observed]), .aura-benefit:not([data-aura-observed])');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (el) {
        el.setAttribute('data-aura-observed', 'true');
        revealNow(el);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          revealNow(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -80px 0px', threshold: 0 }
    );

    Array.prototype.forEach.call(targets, function (el) {
      el.setAttribute('data-aura-observed', 'true');
      var delay = el.getAttribute('data-aura-delay');
      if (delay) {
        el.style.transitionDelay = delay + 'ms';
        var icon = el.querySelector('.aura-benefit__icon');
        var text = el.querySelector('.aura-benefit__text');
        if (icon) icon.style.transitionDelay = delay + 'ms';
        if (text) text.style.transitionDelay = Number(delay) + 50 + 'ms';
      }
      // Already in view on load -> reveal immediately to avoid a flash
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        revealNow(el);
        return;
      }
      observer.observe(el);
    });
  }

  /* ---------------------------------------------------------------- header */

  function initHeader() {
    var header = document.querySelector('[data-aura-header]');
    if (!header) return;

    var onScroll = function () {
      if (window.scrollY > 50) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------------------------------------------------------------- drawer */

  function initDrawer(root) {
    var drawer = root.querySelector('[data-aura-drawer]');
    if (!drawer || drawer.hasAttribute('data-aura-ready')) return;
    drawer.setAttribute('data-aura-ready', 'true');

    var trigger = root.querySelector('[data-aura-drawer-toggle]');
    var backdrop = drawer.querySelector('[data-aura-drawer-backdrop]');
    var closeBtn = drawer.querySelector('[data-aura-drawer-close]');

    function open() {
      drawer.classList.add('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      drawer.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    if (trigger) {
      trigger.addEventListener('click', function () {
        if (drawer.classList.contains('is-open')) {
          close();
        } else {
          open();
        }
      });
    }

    if (backdrop) backdrop.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Drawer links: close first, then scroll (mirrors the original 420ms delay)
    Array.prototype.forEach.call(drawer.querySelectorAll('a[href]'), function (link) {
      link.addEventListener('click', function (event) {
        var href = link.getAttribute('href') || '';
        if (href.charAt(0) !== '#' || href === '#') {
          close();
          return;
        }
        event.preventDefault();
        close();
        window.setTimeout(function () {
          scrollToTarget(href, DRAWER_OFFSET);
        }, 420);
      });
    });

    drawer.__auraClose = close;
  }

  /* ------------------------------------------------------------------- faq */

  function initFaq(root) {
    var items = root.querySelectorAll('[data-aura-faq-item]:not([data-aura-ready])');
    Array.prototype.forEach.call(items, function (item) {
      item.setAttribute('data-aura-ready', 'true');
      var trigger = item.querySelector('[data-aura-faq-trigger]');
      var panel = item.querySelector('[data-aura-faq-panel]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', function () {
        var isOpen = item.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      });
    });
  }

  /* ---------------------------------------------------------------- scroll */

  /* ---------------------------------------------------------------- scroll */

  function scrollToTarget(selector, offset) {
    var id = selector.charAt(0) === '#' ? selector.slice(1) : selector;
    var target = document.getElementById(id);
    if (!target) return;
    var top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  function initAnchors(root) {
    var nodes = root.querySelectorAll('[data-aura-scroll]:not([data-aura-ready])');
    Array.prototype.forEach.call(nodes, function (node) {
      node.setAttribute('data-aura-ready', 'true');
      node.addEventListener('click', function (event) {
        var target = node.getAttribute('data-aura-scroll');
        if (!target || target.charAt(0) !== '#') return;
        if (!document.getElementById(target.slice(1))) return;
        event.preventDefault();
        scrollToTarget(target, HEADER_OFFSET);
      });
    });
  }

  /* ------------------------------------------------------------------ boot */

  function init(root) {
    var scope = root || document;
    initReveal(scope);
    initDrawer(scope);
    initFaq(scope);
    initAnchors(scope);
  }

  function initForms() {
    var forms = document.querySelectorAll('form[data-aura-form]');
    Array.prototype.forEach.call(forms, function (form) {
      form.addEventListener('submit', function () {
        try {
          window.sessionStorage.setItem('aura-form', form.getAttribute('data-aura-form') || '');
        } catch (err) {
          /* ignore */
        }
      });
    });

    var params = new URLSearchParams(window.location.search);
    if (params.get('contact_posted') !== 'true') return;

    var which = '';
    try {
      which = window.sessionStorage.getItem('aura-form') || '';
    } catch (err) {
      which = '';
    }

    var wraps = document.querySelectorAll('[data-aura-form-wrap]');
    Array.prototype.forEach.call(wraps, function (wrap) {
      var name = wrap.getAttribute('data-aura-form-wrap');
      if (!which || which === name) {
        wrap.classList.add('is-sent');
      }
    });
  }

  function boot() {
    initHeader();
    init(document);
    initForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-initialise when a section is re-rendered
  document.addEventListener('shopify:section:load', function (event) {
    initHeader();
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    document.body.style.overflow = '';
  });
})();
